import { AlertCircle, AlertTriangle, Info, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import Button from "./Button";
import { Modal, ModalFooter } from "./Modal";

export type AlertVariant = "error" | "warning" | "info" | "success";

export interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Already user-safe copy — sanitize API text at the call site. */
  message: string;
  closeLabel: string;
  variant: AlertVariant;
  buttonText?: string;
}

const variantConfig = {
  error: {
    icon: AlertCircle,
    iconBg: "bg-error-50 ring-8 ring-error-50/50",
    iconColor: "text-error-600",
  },
  warning: {
    icon: AlertTriangle,
    iconBg: "bg-warning-50 ring-8 ring-warning-50/50",
    iconColor: "text-warning-600",
  },
  info: {
    icon: Info,
    iconBg: "bg-primary-50 ring-8 ring-primary-50/50",
    iconColor: "text-primary-600",
  },
  success: {
    icon: CheckCircle,
    iconBg: "bg-success-50 ring-8 ring-success-50/50",
    iconColor: "text-success-600",
  },
};

/**
 * Full-screen modal for important alerts.
 * Presentation-only: callers must pass sanitized/localized `message`.
 */
export function AlertModal({
  isOpen,
  onClose,
  title,
  message,
  closeLabel,
  variant,
  buttonText,
}: AlertModalProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;
  const resolvedButtonText = buttonText ?? closeLabel;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      title={title}
      description={message}
      showHeader={false}
      size="sm"
      role="alertdialog"
      showCloseButton={false}
      closeOnEscape={false}
      footer={
        <ModalFooter className="justify-center sm:justify-center">
          <Button
            variant="primary"
            onClick={onClose}
            className="min-w-40"
          >
            {resolvedButtonText}
          </Button>
        </ModalFooter>
      }
    >
      <div className="flex flex-col items-center px-2 pb-2 pt-6 text-center sm:px-4">
        <div
          className={cn(
            "mb-5 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full",
            config.iconBg,
          )}
        >
          <Icon
            className={cn("h-6 w-6", config.iconColor)}
          />
        </div>

        <h2 className="text-balance text-lg font-semibold tracking-tight text-surface-900">
          {title}
        </h2>
        <p className="mt-2 max-w-md text-pretty text-sm text-surface-500">
          {message}
        </p>
      </div>
    </Modal>
  );
}
